import React, { useState, useEffect, useRef, useMemo } from 'react';
import Layout from '../components/Layout';
import { getEmployees, getEmployeesPaginated, createEmployee, updateEmployee, deleteEmployee, getDepartments, getDesignations, register, bulkUploadEmployees, downloadEmployeeTemplate, changeEmployeeEmail, reactivateEmployee, rehireEmployee } from '../api';
import { formatUploadError, downloadUploadErrorsCsv, downloadUploadRetryTemplate } from '../utils/uploadErrors';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { UserPlus, Mail, Phone, MapPin, DollarSign, Pencil, Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertCircle, Trash2, Search, Users, Building2, Briefcase, BarChart3, ArrowUpDown, ArrowUp, ArrowDown, Grid3X3, List, Eye, Calendar, ChevronLeft, ChevronRight, Filter, X, ChevronsUpDown, Check, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const EmployeeManagement = ({ user, setUser }) => {
  // Full employees list — used for manager picker, summary cards, and other
  // dialogs that need cross-employee lookups. Loaded once on mount.
  const [employees, setEmployees] = useState([]);
  // Current PAGE of employees coming from the server (for the table/card list).
  const [pagedEmployees, setPagedEmployees] = useState([]);
  // Server-side pagination state
  const [pageTotal, setPageTotal] = useState(0);
  const [pageTotalPages, setPageTotalPages] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [serverSummary, setServerSummary] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailChangeEmployee, setEmailChangeEmployee] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFlagged, setShowFlagged] = useState(false);
  const [activeView, setActiveView] = useState('list');
  const [viewMode, setViewMode] = useState('table'); // 'card' or 'table'
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const triggerRefresh = () => setRefreshTick(t => t + 1);
  const [showFilters, setShowFilters] = useState(false);
  const [managerPopoverOpen, setManagerPopoverOpen] = useState(false);
  const [editManagerPopoverOpen, setEditManagerPopoverOpen] = useState(false);
  // Round 79 — Duplicate prevention & rehire/reactivate
  const [duplicateError, setDuplicateError] = useState(null);       // { code, field, message, matched_*, exit_details }
  const [rehireReactivateOpen, setRehireReactivateOpen] = useState(false);
  const [rehireReactivateReason, setRehireReactivateReason] = useState('');
  const [rehireReactivateSubmitting, setRehireReactivateSubmitting] = useState(false);
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null);  // for override retry
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const canOverride = user?.role === 'admin' || user?.role === 'hr_manager';
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '7579997999',
    employee_id: '',
    department_id: '',
    designation_id: '',
    manager_id: '',
    phone: '',
    address: '',
    date_of_birth: '',
    date_of_joining: '',
    salary: '',
    aadhaar_number: '',
    pan_number: '',
    employment_status: 'Working',
    role: 'employee',
    payout_model: 'fixed'
  });
  const [editFormData, setEditFormData] = useState({
    employee_id: '',
    name: '',
    email: '',
    department_id: '',
    designation_id: '',
    manager_id: '',
    phone: '',
    address: '',
    state: '',
    date_of_birth: '',
    date_of_joining: '',
    salary: '',
    pan_number: '',
    aadhaar_number: '',
    uan_number: '',
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
    employment_status: 'Working',
    gender: '',
    pf_status: 'inactive',
    esic_status: 'inactive',
    medical_insurance_status: 'inactive'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, deptRes, desigRes] = await Promise.all([
        getEmployees(),
        getDepartments(),
        getDesignations()
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
      triggerRefresh();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Debounce search input — avoid hammering the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterDepartment, filterDesignation, filterLocation, filterStatus, sortField, sortDirection, itemsPerPage, showFlagged]);

  // Server-side page fetch — runs on page/filter/search/sort change
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setPageLoading(true);
        const params = {
          page: currentPage,
          page_size: itemsPerPage,
          sort_field: sortField,
          sort_direction: sortDirection,
          include_flagged: showFlagged,
        };
        if (debouncedSearch) params.search = debouncedSearch;
        if (filterDepartment) params.department_id = filterDepartment;
        if (filterDesignation) params.designation_id = filterDesignation;
        if (filterStatus) params.status = filterStatus;
        if (filterLocation) params.location = filterLocation;
        const res = await getEmployeesPaginated(params);
        if (cancelled) return;
        setPagedEmployees(res.data.employees || []);
        setPageTotal(res.data.pagination?.total ?? 0);
        setPageTotalPages(res.data.pagination?.total_pages ?? 1);
        setServerSummary(res.data.summary ?? null);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load employees');
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentPage, itemsPerPage, debouncedSearch, filterDepartment, filterDesignation, filterLocation, filterStatus, sortField, sortDirection, refreshTick]);

  // Get unique locations from employees (used for location filter dropdown).
  const locations = useMemo(
    () => [...new Set(employees.map(emp => emp.work_location || emp.state || emp.address || '').filter(Boolean))],
    [employees]
  );

  // Server is responsible for filter+sort+pagination — the page list is the
  // exact slice we render. We keep these aliases so the JSX below remains
  // backward-compatible without sweeping changes.
  const filteredEmployees = pagedEmployees;
  const sortedEmployees = pagedEmployees;
  const paginatedEmployees = pagedEmployees;
  const totalPages = pageTotalPages;

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterDepartment('');
    setFilterDesignation('');
    setFilterLocation('');
    setFilterStatus('');
  };

  const hasActiveFilters = searchTerm || filterDepartment || filterDesignation || filterLocation || filterStatus;

  // Get summary data — uses server-aggregated counts when available
  // (works correctly even when only one page is loaded). Falls back to the
  // in-memory employees list for the initial render.
  const getDepartmentSummary = () => {
    if (serverSummary?.by_department?.length) {
      return serverSummary.by_department.map(d => [d.name, d.count]);
    }
    const summary = {};
    employees.forEach(emp => {
      const dept = departments.find(d => d.id === emp.department_id);
      const deptName = dept?.name || 'Unassigned';
      summary[deptName] = (summary[deptName] || 0) + 1;
    });
    return Object.entries(summary).sort((a, b) => b[1] - a[1]);
  };

  const getDesignationSummary = () => {
    if (serverSummary?.by_designation?.length) {
      return serverSummary.by_designation.map(d => [d.name, d.count]);
    }
    const summary = {};
    employees.forEach(emp => {
      const desig = designations.find(d => d.id === emp.designation_id);
      const desigName = desig?.title || desig?.name || 'Unassigned';
      summary[desigName] = (summary[desigName] || 0) + 1;
    });
    return Object.entries(summary).sort((a, b) => b[1] - a[1]);
  };

  const getLocationSummary = () => {
    if (serverSummary?.by_location?.length) {
      return serverSummary.by_location.map(d => [d.name, d.count]);
    }
    const summary = {};
    employees.forEach(emp => {
      const location = emp.work_location || emp.state || 'Unspecified';
      summary[location] = (summary[location] || 0) + 1;
    });
    return Object.entries(summary).sort((a, b) => b[1] - a[1]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDuplicateError(null);
    try {
      // First create user
      const userRes = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role || 'employee'
      });

      // Then create employee
      const empPayload = {
        user_id: userRes.data.id,
        employee_id: formData.employee_id,
        department_id: formData.department_id,
        designation_id: formData.designation_id,
        manager_id: formData.manager_id || null,
        phone: formData.phone,
        address: formData.address,
        date_of_birth: formData.date_of_birth,
        date_of_joining: formData.date_of_joining,
        salary: parseFloat(formData.salary),
        payout_model: formData.payout_model || 'fixed',
        aadhaar_number: formData.aadhaar_number || '',
        pan_number: (formData.pan_number || '').toUpperCase(),
        email: formData.email,
        name: formData.full_name,
      };
      const opts = (showOverride && overrideReason.trim())
        ? { override: true, override_reason: overrideReason.trim() }
        : {};
      await createEmployee(empPayload, opts);

      toast.success('Employee created successfully');
      setDialogOpen(false);
      setShowOverride(false);
      setOverrideReason('');
      setDuplicateError(null);
      fetchData();
      setFormData({
        full_name: '',
        email: '',
        password: '7579997999',
        employee_id: '',
        department_id: '',
        designation_id: '',
        manager_id: '',
        phone: '',
        address: '',
        date_of_birth: '',
        date_of_joining: '',
        salary: '',
        aadhaar_number: '',
        pan_number: '',
        payout_model: 'fixed'
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Structured duplicate/format detail
      if (detail && typeof detail === 'object' && detail.code) {
        setDuplicateError(detail);
        setPendingCreatePayload(formData);
        if (detail.code === 'EXITED_MATCH') {
          setRehireReactivateOpen(true);
          setRehireReactivateReason('');
        } else if (detail.code === 'DUPLICATE') {
          toast.error(detail.message || 'Duplicate detected');
        } else if (detail.code === 'INVALID_FORMAT') {
          toast.error(detail.message || 'Invalid format');
        } else {
          toast.error(detail.message || 'Failed');
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create employee');
      }
    }
  };

  const handleReactivate = async () => {
    if (!duplicateError?.matched_employee_id) return;
    if (!rehireReactivateReason.trim()) {
      toast.error('Please enter a reason'); return;
    }
    setRehireReactivateSubmitting(true);
    try {
      await reactivateEmployee(duplicateError.matched_employee_id, { reason: rehireReactivateReason.trim() });
      toast.success('Employee reactivated');
      setRehireReactivateOpen(false);
      setDialogOpen(false);
      setDuplicateError(null);
      setRehireReactivateReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setRehireReactivateSubmitting(false);
    }
  };

  const handleRehire = async () => {
    if (!duplicateError?.matched_employee_id) return;
    if (!rehireReactivateReason.trim()) {
      toast.error('Please enter a reason'); return;
    }
    setRehireReactivateSubmitting(true);
    try {
      const rehirePayload = {
        reason: rehireReactivateReason.trim(),
        new_date_of_joining: formData.date_of_joining || undefined,
        new_designation_id: formData.designation_id || undefined,
        new_department_id: formData.department_id || undefined,
        new_salary: formData.salary ? parseFloat(formData.salary) : undefined,
      };
      await rehireEmployee(duplicateError.matched_employee_id, rehirePayload);
      toast.success('Employee rehired');
      setRehireReactivateOpen(false);
      setDialogOpen(false);
      setDuplicateError(null);
      setRehireReactivateReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rehire');
    } finally {
      setRehireReactivateSubmitting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const response = await bulkUploadEmployees(selectedFile);
      setUploadResult(response.data);
      toast.success(`Employee upload complete: ${response.data.created} created, ${response.data.updated} updated`);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload employee file';
      toast.error(errorMsg);
      setUploadResult({ error: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadEmployeeTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetBulkUpload = () => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditClick = async (employee) => {
    setEditingEmployee(employee);
    setEditFormData({
      employee_id: employee.employee_id || '',
      name: employee.name || '',
      email: employee.email || '',
      department_id: employee.department_id || '',
      designation_id: employee.designation_id || '',
      manager_id: employee.manager_id || '',
      phone: employee.phone || '',
      address: employee.address || '',
      state: employee.state || '',
      date_of_birth: employee.date_of_birth || '',
      date_of_joining: employee.date_of_joining || '',
      salary: employee.salary || '',
      pan_number: employee.pan_number || '',
      aadhaar_number: employee.aadhaar_number || '',
      uan_number: employee.uan_number || '',
      bank_name: employee.bank_name || '',
      bank_account_number: employee.bank_account_number || '',
      ifsc_code: employee.ifsc_code || '',
      employment_status: employee.employment_status || 'Working',
      payout_model: employee.payout_model || 'fixed',
      gender: employee.gender || '',
      pf_status: employee.pf_status || 'inactive',
      esic_status: employee.esic_status || 'inactive',
      medical_insurance_status: employee.medical_insurance_status || 'inactive'
    });
    setEditDialogOpen(true);
    // Refresh designations to pick up any newly created ones
    try {
      const desigRes = await getDesignations();
      setDesignations(desigRes.data);
    } catch (err) {
      // silently fail — existing list is still usable
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateEmployee(editingEmployee.id, {
        user_id: editingEmployee.user_id,
        employee_id: editFormData.employee_id,
        name: editFormData.name,
        email: editFormData.email,
        department_id: editFormData.department_id,
        designation_id: editFormData.designation_id,
        manager_id: editFormData.manager_id || null,
        phone: editFormData.phone,
        address: editFormData.address,
        state: editFormData.state,
        date_of_birth: editFormData.date_of_birth,
        date_of_joining: editFormData.date_of_joining,
        salary: parseFloat(editFormData.salary),
        pan_number: editFormData.pan_number,
        aadhaar_number: editFormData.aadhaar_number,
        uan_number: editFormData.uan_number,
        employment_status: editFormData.employment_status,
        bank_name: editFormData.bank_name,
        bank_account_number: editFormData.bank_account_number,
        ifsc_code: editFormData.ifsc_code,
        payout_model: editFormData.payout_model || 'fixed',
        gender: editFormData.gender,
        pf_status: editFormData.pf_status,
        esic_status: editFormData.esic_status,
        medical_insurance_status: editFormData.medical_insurance_status
      });
      toast.success('Employee updated successfully');
      setEditDialogOpen(false);
      setEditingEmployee(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;
    
    try {
      setDeleting(true);
      await deleteEmployee(employeeToDelete.id);
      toast.success(`Employee ${employeeToDelete.name || employeeToDelete.employee_id} deleted successfully`);
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    } finally {
      setDeleting(false);
    }
  };

  const handleEmailChangeClick = (employee) => {
    setEmailChangeEmployee(employee);
    setNewEmail(employee.email || '');
    setEmailDialogOpen(true);
  };

  const handleEmailChangeConfirm = async () => {
    if (!emailChangeEmployee || !newEmail.trim()) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      setChangingEmail(true);
      await changeEmployeeEmail(emailChangeEmployee.id, newEmail.trim());
      toast.success(`Email changed successfully to ${newEmail.trim()}`);
      setEmailDialogOpen(false);
      setEmailChangeEmployee(null);
      setNewEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change email');
    } finally {
      setChangingEmail(false);
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'N/A';
  };

  const getDesignationTitle = (desigId) => {
    const desig = designations.find(d => d.id === desigId);
    return desig?.title || 'N/A';
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="employee-management">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Employee Management</h1>
            <p className="text-slate-600 mt-1">Manage your workforce ({employees.length || pageTotal} total employees)</p>
          </div>
          <div className="flex gap-3">
            {/* Bulk Upload Button */}
            <Dialog open={bulkUploadOpen} onOpenChange={(open) => {
              setBulkUploadOpen(open);
              if (!open) resetBulkUpload();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-full" data-testid="bulk-upload-btn">
                  <Upload className="mr-2" size={20} />
                  Bulk Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg" data-testid="bulk-upload-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="text-emerald-600" size={24} />
                    Bulk Employee Upload
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Template Download */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600 mb-3">
                      Download the Excel template, fill in employee data, and upload it back.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDownloadTemplate}
                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                      data-testid="download-template-btn"
                    >
                      <Download className="mr-2" size={16} />
                      Download Template
                    </Button>
                  </div>

                  {/* File Upload Area */}
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".xlsx,.xls"
                      className="hidden"
                      id="employee-file-upload"
                      data-testid="file-input"
                    />
                    <label htmlFor="employee-file-upload" className="cursor-pointer">
                      <Upload className="mx-auto text-slate-400 mb-2" size={32} />
                      <p className="text-sm text-slate-600">
                        {selectedFile ? (
                          <span className="text-emerald-600 font-medium">{selectedFile.name}</span>
                        ) : (
                          <>Click to select or drag Excel file here</>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Supports .xlsx and .xls files</p>
                    </label>
                  </div>

                  {/* Upload Button */}
                  <Button
                    onClick={handleBulkUpload}
                    disabled={!selectedFile || uploading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                    data-testid="upload-submit-btn"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2" size={18} />
                        Upload Employees
                      </>
                    )}
                  </Button>

                  {/* Upload Result */}
                  {uploadResult && !uploadResult.error && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="upload-result">
                      <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2">
                        <CheckCircle size={20} />
                        Upload Successful
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-white rounded p-2">
                          <p className="text-slate-500">Created</p>
                          <p className="text-lg font-bold text-emerald-600">{uploadResult.created}</p>
                        </div>
                        <div className="bg-white rounded p-2">
                          <p className="text-slate-500">Updated</p>
                          <p className="text-lg font-bold text-indigo-600">{uploadResult.updated}</p>
                        </div>
                      </div>
                      {uploadResult.note && (
                        <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                          <AlertCircle size={12} className="inline mr-1" />
                          {uploadResult.note}
                        </p>
                      )}
                      {uploadResult.errors?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-emerald-200">
                          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                            <div className="flex items-center gap-1 text-amber-600 text-sm">
                              <AlertCircle size={14} />
                              {uploadResult.errors.length} row(s) had issues:
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => downloadUploadErrorsCsv(uploadResult.errors, 'employee-upload-errors.csv')}
                                data-testid="download-upload-errors-csv"
                              >
                                Download errors (CSV)
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                onClick={() => downloadUploadRetryTemplate(
                                  uploadResult.errors,
                                  uploadResult.column_headers,
                                  'employee-upload-retry.xlsx',
                                )}
                                data-testid="download-upload-retry-template"
                              >
                                Fix & re-upload (Excel)
                              </Button>
                            </div>
                          </div>
                          <ul className="text-xs text-slate-600 max-h-24 overflow-y-auto">
                            {uploadResult.errors.map((err, i) => (
                              <li key={i} className="py-0.5">{formatUploadError(err)}</li>
                            ))}
                          </ul>
                          <p className="text-[10px] text-slate-500 mt-1">
                            Tip: click <b>Fix &amp; re-upload</b> to download only the failed rows as an Excel file (with an <i>_Error Reason</i> hint column). Correct the offending cells and re-upload the same file — the &quot;Error Reason&quot; column is ignored.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {uploadResult?.error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="upload-error">
                      <div className="flex items-center gap-2 text-red-700">
                        <XCircle size={20} />
                        <span className="text-sm">{uploadResult.error}</span>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Single Employee Button */}
            <Dialog open={dialogOpen} onOpenChange={async (open) => {
              setDialogOpen(open);
              if (open) {
                try {
                  const desigRes = await getDesignations();
                  setDesignations(desigRes.data);
                } catch (err) { /* use existing list */ }
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="add-employee-btn">
                  <UserPlus className="mr-2" size={20} />
                  Add Employee
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-employee-dialog">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="employee-form">
                {/* Duplicate / Format error banner (Round 79) */}
                {duplicateError && duplicateError.code !== 'EXITED_MATCH' && (
                  <div className={`p-3 rounded border ${duplicateError.code === 'INVALID_FORMAT' ? 'bg-orange-50 border-orange-300' : 'bg-red-50 border-red-300'}`} data-testid="duplicate-error-banner">
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`h-4 w-4 mt-0.5 ${duplicateError.code === 'INVALID_FORMAT' ? 'text-orange-600' : 'text-red-600'}`} />
                      <div className="flex-1 text-sm">
                        <p className={`font-medium ${duplicateError.code === 'INVALID_FORMAT' ? 'text-orange-900' : 'text-red-900'}`}>
                          {duplicateError.code === 'INVALID_FORMAT' ? 'Invalid Format' : 'Duplicate Detected'}
                        </p>
                        <p className="text-slate-700 mt-1">{duplicateError.message}</p>
                        {duplicateError.matched_employee_code && (
                          <p className="text-xs text-slate-500 mt-1">
                            Match: <b>{duplicateError.matched_employee_code}</b>
                            {duplicateError.matched_employee_name && ` — ${duplicateError.matched_employee_name}`}
                            {duplicateError.matched_employee_status && ` (${duplicateError.matched_employee_status})`}
                          </p>
                        )}
                        {duplicateError.code === 'DUPLICATE' && canOverride && (
                          <div className="mt-3 pt-3 border-t border-red-200">
                            <label className="flex items-center gap-2 text-xs text-red-800">
                              <input
                                type="checkbox"
                                checked={showOverride}
                                onChange={(e) => setShowOverride(e.target.checked)}
                                data-testid="override-checkbox"
                              />
                              I want to override this duplicate check (audit logged)
                            </label>
                            {showOverride && (
                              <Input
                                className="mt-2 h-8 text-xs"
                                placeholder="Reason for override (required) — e.g. legitimate namesake"
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                data-testid="override-reason"
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      required
                      data-testid="full-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Default Password</Label>
                    <Input
                      id="password"
                      type="text"
                      value={formData.password}
                      readOnly
                      className="bg-slate-100 text-slate-600"
                      data-testid="password-input"
                    />
                    <p className="text-xs text-slate-500">Employee can change password after first login</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="employee_id">Employee ID *</Label>
                    <Input
                      id="employee_id"
                      value={formData.employee_id}
                      onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                      required
                      data-testid="employee-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={formData.role} onValueChange={v => setFormData({ ...formData, role: v })}>
                      <SelectTrigger data-testid="role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="ops_manager">Ops Manager</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="hr_executive">HR Executive</SelectItem>
                        <SelectItem value="hr_manager">HR Manager</SelectItem>
                        <SelectItem value="accounts_manager">Accounts Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department_id">Department *</Label>
                    <Select
                      value={formData.department_id}
                      onValueChange={(value) => setFormData({ ...formData, department_id: value, designation_id: '' })}
                      required
                    >
                      <SelectTrigger data-testid="department-select">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map(dept => (
                          <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="designation_id">Designation *</Label>
                    <Select
                      value={formData.designation_id}
                      onValueChange={(value) => setFormData({ ...formData, designation_id: value })}
                      required
                      disabled={!formData.department_id}
                    >
                      <SelectTrigger data-testid="designation-select">
                        <SelectValue placeholder={formData.department_id ? "Select designation" : "Select department first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {designations
                          .filter(desig => desig.department_id === formData.department_id)
                          .map(desig => (
                            <SelectItem key={desig.id} value={desig.id}>{desig.title}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="manager_id">Reporting Manager</Label>
                    <Popover open={managerPopoverOpen} onOpenChange={setManagerPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={managerPopoverOpen}
                          className="w-full justify-between font-normal"
                          data-testid="manager-select"
                        >
                          {formData.manager_id
                            ? employees.find(emp => emp.id === formData.manager_id)?.name || 
                              employees.find(emp => emp.id === formData.manager_id)?.full_name || 
                              "Select manager..."
                            : "No Manager (Top Level)"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search employee..." />
                          <CommandList>
                            <CommandEmpty>No employee found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="none"
                                onSelect={() => {
                                  setFormData({ ...formData, manager_id: '' });
                                  setManagerPopoverOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", !formData.manager_id ? "opacity-100" : "opacity-0")} />
                                No Manager (Top Level)
                              </CommandItem>
                              {employees
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map(emp => (
                                  <CommandItem
                                    key={emp.id}
                                    value={`${emp.name || emp.full_name} ${emp.designation_name || ''}`}
                                    onSelect={() => {
                                      setFormData({ ...formData, manager_id: emp.id });
                                      setManagerPopoverOpen(false);
                                    }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", formData.manager_id === emp.id ? "opacity-100" : "opacity-0")} />
                                    {emp.name || emp.full_name} {emp.designation_name ? `(${emp.designation_name})` : ''}
                                  </CommandItem>
                                ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-gray-500">Who will this employee report to?</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_joining">Date of Joining *</Label>
                    <Input
                      id="date_of_joining"
                      type="date"
                      value={formData.date_of_joining}
                      onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                      required
                      data-testid="date-joining-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salary">Salary *</Label>
                    <Input
                      id="salary"
                      type="number"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      required
                      data-testid="salary-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payout_model">Payout Model *</Label>
                    <Select value={formData.payout_model} onValueChange={(v) => setFormData({ ...formData, payout_model: v })}>
                      <SelectTrigger data-testid="payout-model-select">
                        <SelectValue placeholder="Select payout model" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">Fixed Salary</SelectItem>
                        <SelectItem value="ppu">Pay Per Unit (PPU)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      data-testid="date-birth-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      data-testid="address-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aadhaar_number">Aadhaar Number</Label>
                    <Input
                      id="aadhaar_number"
                      value={formData.aadhaar_number || ''}
                      onChange={(e) => setFormData({ ...formData, aadhaar_number: e.target.value.replace(/\D/g, '') })}
                      placeholder="12 digits"
                      maxLength={12}
                      data-testid="aadhaar-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pan_number">PAN Number</Label>
                    <Input
                      id="pan_number"
                      value={formData.pan_number || ''}
                      onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                      placeholder="AAAAA9999A"
                      maxLength={10}
                      data-testid="pan-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="submit-employee-btn">
                  Create Employee
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Rehire / Reactivate Dialog — Round 79 */}
          <Dialog open={rehireReactivateOpen} onOpenChange={setRehireReactivateOpen}>
            <DialogContent className="max-w-lg" data-testid="rehire-reactivate-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  Exited Employee Match
                </DialogTitle>
                <DialogDescription>
                  {duplicateError?.message}
                </DialogDescription>
              </DialogHeader>
              {duplicateError && (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-50 border rounded text-sm">
                    <p><b>{duplicateError.matched_employee_code}</b> — {duplicateError.matched_employee_name}</p>
                    <p className="text-xs text-slate-500">Status: {duplicateError.matched_employee_status}</p>
                    {duplicateError.exit_details && (
                      <div className="text-xs text-slate-600 mt-2 space-y-0.5">
                        {duplicateError.exit_details.last_working_day && (
                          <p>Last working day: {duplicateError.exit_details.last_working_day}</p>
                        )}
                        {duplicateError.exit_details.exit_reason && (
                          <p>Exit reason: {duplicateError.exit_details.exit_reason}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Reason (required)</Label>
                    <Input
                      value={rehireReactivateReason}
                      onChange={(e) => setRehireReactivateReason(e.target.value)}
                      placeholder="e.g. Returning after maternity break / new role after 6 months"
                      data-testid="rehire-reason-input"
                    />
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                    <p><b>Reactivate</b> — restores the existing record (use when exit was accidental or brief break).</p>
                    <p className="mt-1"><b>Rehire</b> — starts a new employment tenure, retains prior history (use for a fresh engagement).</p>
                  </div>
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setRehireReactivateOpen(false)}>Cancel</Button>
                <Button
                  variant="outline"
                  className="border-blue-500 text-blue-700 hover:bg-blue-50"
                  onClick={handleReactivate}
                  disabled={rehireReactivateSubmitting || !canOverride}
                  data-testid="reactivate-btn"
                >
                  Reactivate
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleRehire}
                  disabled={rehireReactivateSubmitting || !canOverride}
                  data-testid="rehire-btn"
                >
                  Rehire (New Tenure)
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveView('list')}
            className={`px-4 py-2 font-medium flex items-center gap-2 ${activeView === 'list' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            <Users size={18} /> Employee List
          </button>
          <button
            onClick={() => setActiveView('summary')}
            className={`px-4 py-2 font-medium flex items-center gap-2 ${activeView === 'summary' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            <BarChart3 size={18} /> Summary
          </button>
        </div>

        {/* Summary View */}
        {activeView === 'summary' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Department Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Department Wise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {getDepartmentSummary().map(([name, count]) => (
                    <div key={name} className="flex justify-between items-center p-2 bg-gray-50 rounded hover:bg-blue-50 cursor-pointer" onClick={() => {
                      const dept = departments.find(d => d.name === name);
                      if (dept) {
                        setFilterDepartment(dept.id);
                        setActiveView('list');
                      }
                    }}>
                      <span className="text-sm text-gray-700 truncate">{name}</span>
                      <span className="text-sm font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">{count}</span>
                    </div>
                  ))}
                  {getDepartmentSummary().length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Designation Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-green-600" />
                  Designation Wise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {getDesignationSummary().map(([name, count]) => (
                    <div key={name} className="flex justify-between items-center p-2 bg-gray-50 rounded hover:bg-green-50 cursor-pointer" onClick={() => {
                      const desig = designations.find(d => (d.title || d.name) === name);
                      if (desig) {
                        setFilterDesignation(desig.id);
                        setActiveView('list');
                      }
                    }}>
                      <span className="text-sm text-gray-700 truncate">{name}</span>
                      <span className="text-sm font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded">{count}</span>
                    </div>
                  ))}
                  {getDesignationSummary().length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Location Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  Location Wise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {getLocationSummary().map(([name, count]) => (
                    <div key={name} className="flex justify-between items-center p-2 bg-gray-50 rounded hover:bg-purple-50 cursor-pointer" onClick={() => {
                      setFilterLocation(name);
                      setActiveView('list');
                    }}>
                      <span className="text-sm text-gray-700 truncate">{name}</span>
                      <span className="text-sm font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded">{count}</span>
                    </div>
                  ))}
                  {getLocationSummary().length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-4">No data</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* List View with Search and Filters */}
        {activeView === 'list' && (
          <>
            {/* Enhanced Search and Filters */}
            <div className="bg-white rounded-lg border shadow-sm">
              {/* Top Bar - Search, View Toggle, Sort */}
              <div className="p-4 border-b">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Search */}
                  <div className="flex-1 min-w-[300px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, ID, email or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="employee-search"
                    />
                    {searchTerm && (
                      <button 
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Filter Toggle */}
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filters
                    {hasActiveFilters && (
                      <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                        {[filterDepartment, filterDesignation, filterLocation, filterStatus].filter(Boolean).length}
                      </span>
                    )}
                  </Button>

                  {/* Sort Dropdown */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Sort:</span>
                    <select
                      value={sortField}
                      onChange={(e) => setSortField(e.target.value)}
                      className="border rounded-md px-3 py-2 text-sm bg-white"
                    >
                      <option value="name">Name</option>
                      <option value="employee_id">Employee ID</option>
                      <option value="department">Department</option>
                      <option value="designation">Designation</option>
                      <option value="joining_date">Joining Date</option>
                      <option value="salary">Salary</option>
                      <option value="status">Status</option>
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                      className="px-2"
                    >
                      {sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                    </Button>
                  </div>

                  {/* View Toggle */}
                  <div className="flex border rounded-md overflow-hidden">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`p-2 ${viewMode === 'card' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                    >
                      <Grid3X3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Expandable Filters */}
              {showFilters && (
                <div className="p-4 bg-gray-50 border-b">
                  <div className="flex flex-wrap gap-4">
                    {/* Department Filter */}
                    <div className="min-w-[180px]">
                      <label className="text-xs text-gray-500 mb-1 block">Department</label>
                      <select
                        value={filterDepartment}
                        onChange={(e) => { setFilterDepartment(e.target.value); setFilterDesignation(''); }}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="department-filter"
                      >
                        <option value="">All Departments</option>
                        {departments.map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Designation Filter */}
                    <div className="min-w-[180px]">
                      <label className="text-xs text-gray-500 mb-1 block">Designation</label>
                      <select
                        value={filterDesignation}
                        onChange={(e) => setFilterDesignation(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="designation-filter"
                      >
                        <option value="">All Designations</option>
                        {designations
                          .filter(d => !filterDepartment || d.department_id === filterDepartment)
                          .map(d => (
                            <option key={d.id} value={d.id}>{d.title || d.name}</option>
                          ))}
                      </select>
                    </div>

                    {/* Location Filter */}
                    <div className="min-w-[180px]">
                      <label className="text-xs text-gray-500 mb-1 block">Location</label>
                      <select
                        value={filterLocation}
                        onChange={(e) => setFilterLocation(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="location-filter"
                      >
                        <option value="">All Locations</option>
                        {locations.map(loc => (
                          <option key={loc} value={loc}>{loc}</option>
                        ))}
                      </select>
                    </div>

                    {/* Status Filter */}
                    <div className="min-w-[150px]">
                      <label className="text-xs text-gray-500 mb-1 block">Status</label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                      >
                        <option value="">All Status</option>
                        <option value="Working">Working</option>
                        <option value="Exit">Exit</option>
                        <option value="Probation">Probation</option>
                        <option value="Notice Period">Notice Period</option>
                      </select>
                    </div>

                    {/* Show Flagged Duplicates Toggle */}
                    <div className="min-w-[180px] flex items-end">
                      <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="show-flagged-toggle">
                        <input
                          type="checkbox"
                          checked={showFlagged}
                          onChange={(e) => setShowFlagged(e.target.checked)}
                          className="rounded"
                        />
                        Show flagged duplicates
                      </label>
                    </div>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                      <div className="flex items-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearFilters}
                          className="gap-2 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                          Clear All
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Results Info & Pagination Controls */}
              <div className="p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold">{pageTotal === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}</span> - <span className="font-semibold">{Math.min(currentPage * itemsPerPage, pageTotal)}</span> of <span className="font-semibold">{pageTotal}</span> employees
                  {hasActiveFilters && <span className="text-blue-600 ml-2">(filtered from {employees.length})</span>}
                  {pageLoading && <span className="text-slate-400 ml-2">…loading</span>}
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Items per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Show:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="border rounded-md px-2 py-1 text-sm bg-white"
                      data-testid="employees-page-size"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-2"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            className="px-3"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-2"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Table View */}
            {viewMode === 'table' && (
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('employee_id')}>
                          <div className="flex items-center gap-2">
                            ID {sortField === 'employee_id' && (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                          <div className="flex items-center gap-2">
                            Name {sortField === 'name' && (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('department')}>
                          <div className="flex items-center gap-2">
                            Department {sortField === 'department' && (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('designation')}>
                          <div className="flex items-center gap-2">
                            Designation {sortField === 'designation' && (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th className="text-left p-4 font-medium text-gray-700">Contact</th>
                        <th className="text-left p-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                          <div className="flex items-center gap-2">
                            Status {sortField === 'status' && (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                          </div>
                        </th>
                        <th className="text-right p-4 font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEmployees.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-gray-500">
                            {employees.length === 0 ? 'No employees found. Add your first employee!' : 'No employees match your search criteria.'}
                          </td>
                        </tr>
                      ) : (
                        paginatedEmployees.map((emp) => {
                          const dept = departments.find(d => d.id === emp.department_id);
                          const desig = designations.find(d => d.id === emp.designation_id);
                          return (
                            <tr key={emp.id} className={`border-b hover:bg-gray-50 ${emp.duplicate_flagged ? 'bg-red-50/50' : ''}`}>
                              <td className="p-4 font-mono text-sm text-gray-600">
                                {emp.employee_id || '-'}
                                {emp.duplicate_flagged && (
                                  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] uppercase font-semibold" title={`Flagged as duplicate — merged into ${emp.merged_into || 'another record'}`} data-testid={`flagged-badge-${emp.id}`}>
                                    <ShieldAlert className="h-2.5 w-2.5" />
                                    Duplicate
                                  </span>
                                )}
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                                    {(emp.name || emp.first_name || 'U').charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`}</p>
                                    <p className="text-xs text-gray-500">{emp.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4 text-gray-600">{dept?.name || '-'}</td>
                              <td className="p-4 text-gray-600">{desig?.title || desig?.name || '-'}</td>
                              <td className="p-4">
                                <p className="text-sm text-gray-600">{emp.phone || '-'}</p>
                              </td>
                              <td className="p-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  emp.employment_status === 'Working' ? 'bg-green-100 text-green-700' :
                                  emp.employment_status === 'Exit' ? 'bg-red-100 text-red-700' :
                                  emp.employment_status === 'Probation' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {emp.employment_status || 'Working'}
                                </span>
                              </td>
                              <td className="p-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <Button variant="ghost" size="sm" onClick={() => handleEmailChangeClick(emp)} title="Change Email">
                                    <Mail className="h-4 w-4 text-sky-500" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleEditClick(emp)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => { setEmployeeToDelete(emp); setDeleteDialogOpen(true); }}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Card View */}
            {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedEmployees.length === 0 ? (
            <div className="col-span-3 text-center py-12">
              <p className="text-slate-500">{employees.length === 0 ? 'No employees found. Add your first employee!' : 'No employees match your search criteria.'}</p>
            </div>
          ) : (
            paginatedEmployees.map((emp, index) => (
              <Card key={emp.id} className="border border-slate-100 shadow-sm card-hover" data-testid={`employee-card-${index}`}>
                <CardHeader className="border-b border-slate-50">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 ${emp.employment_status === 'Exit' ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'} rounded-full flex items-center justify-center font-bold`}>
                        {emp.name?.substring(0, 2).toUpperCase() || emp.employee_id?.substring(0, 2) || 'EM'}
                      </div>
                      <div>
                        <p className="text-slate-900 font-semibold flex items-center gap-2" data-testid={`employee-name-${index}`}>
                          {emp.name || 'N/A'}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${emp.employment_status === 'Exit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {emp.employment_status || 'Working'}
                          </span>
                          {emp.duplicate_flagged && (
                            <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 inline-flex items-center gap-1" title={`Flagged as duplicate — merged into ${emp.merged_into || 'another record'}`}>
                              <ShieldAlert className="h-2.5 w-2.5" />
                              Duplicate
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500" data-testid={`employee-id-${index}`}>{emp.employee_id}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(user?.role === 'admin' || user?.role === 'hr_manager') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEmailChangeClick(emp)}
                          className="text-sky-600 hover:bg-sky-50"
                          title="Change Email"
                          data-testid={`change-email-btn-${index}`}
                        >
                          <Mail size={16} />
                        </Button>
                      )}
                      {(user?.role === 'admin' || user?.role === 'hr_manager') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEditClick(emp)}
                          className="text-indigo-600 hover:bg-indigo-50"
                          data-testid={`edit-employee-btn-${index}`}
                        >
                          <Pencil size={16} />
                        </Button>
                      )}
                      {(user?.role === 'admin' || user?.role === 'hr_manager') && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteClick(emp)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-employee-btn-${index}`}
                          title="Soft-delete (only Admin can restore)"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-3">
                  {emp.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={16} className="text-sky-400" />
                      <span className="text-slate-600 truncate" title={emp.email}>{emp.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 size={16} className="text-slate-400" />
                    <span className="text-slate-600">Department: {getDepartmentName(emp.department_id)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={16} className="text-slate-400" />
                    <span className="text-slate-600">{emp.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={16} className="text-slate-400" />
                    <span className="text-slate-600">{getDesignationTitle(emp.designation_id)}</span>
                  </div>
                  {emp.state && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin size={16} className="text-blue-400" />
                      <span className="text-blue-600 font-medium">{emp.state}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign size={16} className="text-slate-400" />
                    <span className="text-slate-600 font-medium mono">₹{emp.salary?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="pt-2 border-t border-slate-100">
                    <p className="text-xs text-slate-500">Joined: {emp.date_of_joining}</p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
            )}
        </>
        )}

        {/* Edit Employee Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-employee-dialog">
            <DialogHeader>
              <DialogTitle>Edit Employee - {editingEmployee?.employee_id}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_name">Employee Name *</Label>
                  <Input
                    id="edit_name"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_employee_id">Employee ID *</Label>
                  <Input
                    id="edit_employee_id"
                    value={editFormData.employee_id}
                    onChange={(e) => setEditFormData({ ...editFormData, employee_id: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_department_id">Department *</Label>
                  <Select
                    value={editFormData.department_id}
                    onValueChange={(value) => setEditFormData({ ...editFormData, department_id: value, designation_id: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map(dept => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_designation_id">Designation *</Label>
                  <Select
                    value={editFormData.designation_id}
                    onValueChange={(value) => setEditFormData({ ...editFormData, designation_id: value })}
                    disabled={!editFormData.department_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={editFormData.department_id ? "Select designation" : "Select department first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {designations
                        .filter(desig => desig.department_id === editFormData.department_id)
                        .map(desig => (
                          <SelectItem key={desig.id} value={desig.id}>{desig.title}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_manager_id">Reporting Manager</Label>
                  <Popover open={editManagerPopoverOpen} onOpenChange={setEditManagerPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={editManagerPopoverOpen}
                        className="w-full justify-between font-normal"
                      >
                        {editFormData.manager_id
                          ? employees.find(emp => emp.id === editFormData.manager_id)?.name || 
                            employees.find(emp => emp.id === editFormData.manager_id)?.full_name || 
                            "Select manager..."
                          : "No Manager (Top Level)"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search employee..." />
                        <CommandList>
                          <CommandEmpty>No employee found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="none"
                              onSelect={() => {
                                setEditFormData({ ...editFormData, manager_id: '' });
                                setEditManagerPopoverOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", !editFormData.manager_id ? "opacity-100" : "opacity-0")} />
                              No Manager (Top Level)
                            </CommandItem>
                            {employees
                              .filter(emp => emp.id !== editingEmployee?.id)
                              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                              .map(emp => (
                                <CommandItem
                                  key={emp.id}
                                  value={`${emp.name || emp.full_name} ${emp.designation_name || ''}`}
                                  onSelect={() => {
                                    setEditFormData({ ...editFormData, manager_id: emp.id });
                                    setEditManagerPopoverOpen(false);
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4", editFormData.manager_id === emp.id ? "opacity-100" : "opacity-0")} />
                                  {emp.name || emp.full_name} {emp.designation_name ? `(${emp.designation_name})` : ''}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500">This employee will report to the selected manager for leave approvals</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_phone">Phone *</Label>
                  <Input
                    id="edit_phone"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_date_of_joining">Date of Joining *</Label>
                  <Input
                    id="edit_date_of_joining"
                    type="date"
                    value={editFormData.date_of_joining}
                    onChange={(e) => setEditFormData({ ...editFormData, date_of_joining: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_salary">Salary (₹) *</Label>
                  <Input
                    id="edit_salary"
                    type="number"
                    value={editFormData.salary}
                    onChange={(e) => setEditFormData({ ...editFormData, salary: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_payout_model">Payout Model</Label>
                  <Select value={editFormData.payout_model || 'fixed'} onValueChange={(v) => setEditFormData({ ...editFormData, payout_model: v })}>
                    <SelectTrigger data-testid="edit-payout-model-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed Salary</SelectItem>
                      <SelectItem value="ppu">Pay Per Unit (PPU)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    data-testid="edit-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_gender">Gender</Label>
                  <Select value={editFormData.gender || 'not_set'} onValueChange={(v) => setEditFormData({ ...editFormData, gender: v === 'not_set' ? '' : v })}>
                    <SelectTrigger data-testid="edit-gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_set">Not Set</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Statutory Status Section */}
                <div className="col-span-2 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700 mb-3">Statutory Compliance Status</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_pf_status">PF Status</Label>
                  <Select value={editFormData.pf_status || 'inactive'} onValueChange={(v) => setEditFormData({ ...editFormData, pf_status: v })}>
                    <SelectTrigger data-testid="edit-pf-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_esic_status">ESIC Status</Label>
                  <Select value={editFormData.esic_status || 'inactive'} onValueChange={(v) => setEditFormData({ ...editFormData, esic_status: v })}>
                    <SelectTrigger data-testid="edit-esic-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_medical_insurance">Medical Insurance</Label>
                  <Select value={editFormData.medical_insurance_status || 'inactive'} onValueChange={(v) => setEditFormData({ ...editFormData, medical_insurance_status: v })}>
                    <SelectTrigger data-testid="edit-medical-insurance">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700 mb-3">Personal Details</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_date_of_birth">Date of Birth</Label>
                  <Input
                    id="edit_date_of_birth"
                    type="date"
                    value={editFormData.date_of_birth}
                    onChange={(e) => setEditFormData({ ...editFormData, date_of_birth: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_address">Address</Label>
                  <Input
                    id="edit_address"
                    value={editFormData.address}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_state">State (for Compliance)</Label>
                  <Select
                    value={editFormData.state}
                    onValueChange={(value) => setEditFormData({ ...editFormData, state: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Andhra Pradesh">Andhra Pradesh</SelectItem>
                      <SelectItem value="Assam">Assam</SelectItem>
                      <SelectItem value="Bihar">Bihar</SelectItem>
                      <SelectItem value="Chhattisgarh">Chhattisgarh</SelectItem>
                      <SelectItem value="Delhi">Delhi</SelectItem>
                      <SelectItem value="Goa">Goa</SelectItem>
                      <SelectItem value="Gujarat">Gujarat</SelectItem>
                      <SelectItem value="Haryana">Haryana</SelectItem>
                      <SelectItem value="Himachal Pradesh">Himachal Pradesh</SelectItem>
                      <SelectItem value="Jharkhand">Jharkhand</SelectItem>
                      <SelectItem value="Karnataka">Karnataka</SelectItem>
                      <SelectItem value="Kerala">Kerala</SelectItem>
                      <SelectItem value="Madhya Pradesh">Madhya Pradesh</SelectItem>
                      <SelectItem value="Maharashtra">Maharashtra</SelectItem>
                      <SelectItem value="Manipur">Manipur</SelectItem>
                      <SelectItem value="Meghalaya">Meghalaya</SelectItem>
                      <SelectItem value="Odisha">Odisha</SelectItem>
                      <SelectItem value="Punjab">Punjab</SelectItem>
                      <SelectItem value="Rajasthan">Rajasthan</SelectItem>
                      <SelectItem value="Sikkim">Sikkim</SelectItem>
                      <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
                      <SelectItem value="Telangana">Telangana</SelectItem>
                      <SelectItem value="Tripura">Tripura</SelectItem>
                      <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
                      <SelectItem value="Uttarakhand">Uttarakhand</SelectItem>
                      <SelectItem value="West Bengal">West Bengal</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Used for PT & LWF calculations</p>
                </div>
                
                {/* Employment Status */}
                <div className="col-span-2 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700 mb-3">Employment Status</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_employment_status">Status *</Label>
                  <Select
                    value={editFormData.employment_status}
                    onValueChange={(value) => setEditFormData({ ...editFormData, employment_status: value })}
                  >
                    <SelectTrigger className={editFormData.employment_status === 'Exit' ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Working">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-green-500"></span>
                          Working
                        </span>
                      </SelectItem>
                      <SelectItem value="Exit">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-red-500"></span>
                          Exit
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div></div>
                
                {/* KYC Details */}
                <div className="col-span-2 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700 mb-3">KYC Details</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_pan_number">PAN Number</Label>
                  <Input
                    id="edit_pan_number"
                    value={editFormData.pan_number}
                    onChange={(e) => setEditFormData({ ...editFormData, pan_number: e.target.value.toUpperCase() })}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_aadhaar_number">Aadhaar Number</Label>
                  <Input
                    id="edit_aadhaar_number"
                    value={editFormData.aadhaar_number}
                    onChange={(e) => setEditFormData({ ...editFormData, aadhaar_number: e.target.value })}
                    placeholder="1234 5678 9012"
                    maxLength={14}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_uan_number">UAN Number</Label>
                  <Input
                    id="edit_uan_number"
                    value={editFormData.uan_number || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, uan_number: e.target.value })}
                    placeholder="100123456789"
                    maxLength={12}
                  />
                </div>
                
                {/* Bank Details */}
                <div className="col-span-2 pt-4 border-t">
                  <h3 className="font-semibold text-slate-700 mb-3">Bank Details</h3>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_bank_name">Bank Name</Label>
                  <Input
                    id="edit_bank_name"
                    value={editFormData.bank_name}
                    onChange={(e) => setEditFormData({ ...editFormData, bank_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_bank_account_number">Account Number</Label>
                  <Input
                    id="edit_bank_account_number"
                    value={editFormData.bank_account_number}
                    onChange={(e) => setEditFormData({ ...editFormData, bank_account_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_ifsc_code">IFSC Code</Label>
                  <Input
                    id="edit_ifsc_code"
                    value={editFormData.ifsc_code}
                    onChange={(e) => setEditFormData({ ...editFormData, ifsc_code: e.target.value.toUpperCase() })}
                    placeholder="HDFC0001234"
                    maxLength={11}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1 rounded-full"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full">
                  Update Employee
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 size={20} />
                Delete Employee
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                This action cannot be undone. This will permanently delete the employee and all associated data.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  Are you sure you want to delete <strong>{employeeToDelete?.name || employeeToDelete?.employee_id}</strong>?
                </p>
                <p className="text-xs text-red-600 mt-2">
                  This will also delete:
                </p>
                <ul className="text-xs text-red-600 list-disc list-inside mt-1">
                  <li>User account and login access</li>
                  <li>All payroll records</li>
                  <li>All attendance records</li>
                  <li>All leave records</li>
                </ul>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setEmployeeToDelete(null);
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Deleting...' : 'Delete Employee'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Email Dialog */}
        <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-sky-600 flex items-center gap-2">
                <Mail size={20} />
                Change Employee Email
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Update the login email for {emailChangeEmployee?.name || emailChangeEmployee?.employee_id}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                <p className="text-sm text-sky-800">
                  <strong>{emailChangeEmployee?.name}</strong> ({emailChangeEmployee?.employee_id})
                </p>
                <p className="text-xs text-sky-600 mt-1">Current email: {emailChangeEmployee?.email || 'Not set'}</p>
              </div>
              <div>
                <Label htmlFor="new-email">New Email Address *</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  data-testid="change-email-input"
                />
                <p className="text-xs text-slate-500 mt-1">This will change the employee&apos;s login email</p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setEmailDialogOpen(false); setEmailChangeEmployee(null); }} disabled={changingEmail}>Cancel</Button>
              <Button onClick={handleEmailChangeConfirm} disabled={changingEmail || !newEmail.trim()} className="bg-sky-600 hover:bg-sky-700" data-testid="change-email-submit">
                {changingEmail ? 'Updating...' : 'Update Email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default EmployeeManagement;